import React from 'react';
import { SocialUser } from '../../services/socialService';
import { getAvatarUrl } from '../../utils/avatarUtils';
import logo from '../../assets/logo.png';

interface ProvokeShareCardProps {
  currentUser: { uid: string; displayName: string | null; photoURL?: string | null } | null;
  friend: SocialUser | undefined;
  provokeMessage: string;
  provokeContext: string;
  cardRef: React.RefObject<HTMLDivElement>;
}

export default function ProvokeShareCard({ currentUser, friend, provokeMessage, provokeContext, cardRef }: ProvokeShareCardProps) {
  const getInitials = (name?: string) => {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const myAvatarUrl = currentUser?.photoURL || getAvatarUrl(currentUser?.uid || 'user');
  const friendAvatarUrl = friend?.profilePhotoUrl || getAvatarUrl(friend?.username || friend?.id || 'friend');

  return (
    <div className="fixed top-[200vh] left-[200vw] pointer-events-none z-[-100]">
      <div ref={cardRef} style={{
        width: '600px',
        height: '400px',
        background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 40%, #991b1b 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '28px 32px 24px',
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        borderRadius: '0',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative background elements */}
        <div style={{
          position: 'absolute', top: '-60px', right: '-60px',
          width: '200px', height: '200px',
          background: 'radial-gradient(circle, rgba(251,191,36,0.15) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />
        <div style={{
          position: 'absolute', bottom: '-40px', left: '-40px',
          width: '160px', height: '160px',
          background: 'radial-gradient(circle, rgba(251,191,36,0.1) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />

        {/* Title */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '24px', position: 'relative', zIndex: 1, width: '100%' }}>
          <img referrerPolicy="no-referrer" src={logo} alt="Logo" style={{ width: '80px', height: '80px', position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)' }} crossOrigin="anonymous" />
          <span style={{ fontSize: '50px', lineHeight: 1 }}>🔥</span>
          <h1 style={{
            fontSize: '36px',
            fontWeight: 900,
            letterSpacing: '-0.5px',
            textTransform: 'uppercase',
            margin: 0,
            textShadow: '0 2px 8px rgba(0,0,0,0.3)'
          }}>Provocation!</h1>
        </div>

        {/* Avatar Circles */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', position: 'relative', zIndex: 1 }}>
          <div style={{
            width: '64px', height: '64px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
            border: '3px solid white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '22px', fontWeight: 800, color: 'white',
            letterSpacing: '1px',
            overflow: 'hidden'
          }}>
            {currentUser?.photoURL ? (
               <img referrerPolicy="no-referrer" src={currentUser.photoURL} alt="You" style={{ width: '100%', height: '100%', objectFit: 'cover' }} crossOrigin="anonymous" />
            ) : (
               <span>{getInitials(currentUser?.displayName || 'User')}</span>
            )}
          </div>
          <span style={{ fontSize: '24px', fontWeight: 800, color: 'rgba(255,255,255,0.6)', margin: '0 2px' }}>vs</span>
          <div style={{
            width: '64px', height: '64px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #a855f7, #ec4899)',
            border: '3px solid rgba(255,255,255,0.7)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '22px', fontWeight: 800, color: 'white',
            letterSpacing: '1px',
            overflow: 'hidden'
          }}>
            {friend?.profilePhotoUrl ? (
               <img referrerPolicy="no-referrer" src={friend.profilePhotoUrl} alt="Friend" style={{ width: '100%', height: '100%', objectFit: 'cover' }} crossOrigin="anonymous" />
            ) : (
               <span>{getInitials(friend?.username)}</span>
            )}
          </div>
        </div>

        {/* Message Card */}
        <div style={{
          background: 'rgba(255,255,255,0.12)',
          borderRadius: '16px',
          padding: '20px 28px',
          width: '100%',
          maxWidth: '500px',
          border: '1px solid rgba(255,255,255,0.18)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1
        }}>
          <p style={{
            fontSize: '20px',
            fontWeight: 700,
            fontStyle: 'italic',
            lineHeight: 1.4,
            margin: '0 0 10px',
            textShadow: '0 1px 4px rgba(0,0,0,0.2)'
          }}>"{provokeMessage}"</p>
          <p style={{
            fontSize: '11px',
            color: 'rgba(255,200,200,0.9)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            margin: 0,
            lineHeight: 1.4
          }}>— {provokeContext || `${currentUser?.displayName || 'Someone'} poking ${friend?.username || 'a friend'}`}</p>
        </div>

        {/* Footer */}
        <div style={{
          marginTop: '16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1
        }}>
          <p style={{
            color: 'rgba(255,255,255,0.7)',
            fontWeight: 700,
            textTransform: 'uppercase',
            fontSize: '10px',
            letterSpacing: '2px',
            margin: '0 0 6px'
          }}>Let's play together, add me: <span style={{ color: 'white', fontWeight: 800 }}>{currentUser?.displayName || ''}</span></p>
          <p style={{
            color: 'white',
            fontWeight: 700,
            fontSize: '12px',
            background: 'rgba(0,0,0,0.25)',
            padding: '4px 16px',
            borderRadius: '999px',
            margin: 0
          }}>lingualearn.web.app</p>
        </div>
      </div>
    </div>
  );
}
